import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../../../contexts/AuthContext";

function RulesList({ refreshTrigger, onEdit, onDelete }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { apiBaseUrl } = useAuth();

  useEffect(() => {
    fetchRules();
  }, [refreshTrigger]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/api/transfer-market/admin/rules`
      );
      setRules(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error("❌ خطا در دریافت قوانین:", error);
      setLoading(false);
      toast.error("خطا در دریافت قوانین");
    }
  };

  const handleDelete = async (rule) => {
    const isConfirmed = window.confirm(
      `آیا از حذف این قانون اطمینان دارید؟\n\nمدل: ${rule.model}\nفیلد: ${rule.field}\nاهرم: ${rule.leverage}x`
    );

    if (!isConfirmed) return;

    try {
      setDeletingId(rule.id);
      await axios.delete(
        `${apiBaseUrl}/api/transfer-market/admin/rules/${rule.id}`
      );
      toast.success("قانون با موفقیت حذف شد");
      fetchRules();
    } catch (error) {
      console.error("❌ خطا در حذف قانون:", error);
      toast.error("خطا در حذف قانون");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">لیست قوانین</h3>
          <p className="text-sm text-gray-400 mt-1">
            مشاهده و مدیریت قوانین بازار نقل و انتقالات
          </p>
        </div>
        <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg px-4 py-2">
          <span className="text-blue-400 font-semibold">
            {rules.length} قانون
          </span>
        </div>
      </div>

      {rules.length === 0 ? (
        <div className="bg-gray-700/30 rounded-lg p-12 text-center border border-gray-600">
          <svg
            className="w-16 h-16 text-gray-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-400">هیچ قانونی یافت نشد</p>
        </div>
      ) : (
        <div className="bg-gray-700/30 rounded-lg border border-gray-600 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                    شناسه
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                    مدل
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                    فیلد
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
                    اهرم
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {rules.map((rule, index) => (
                  <motion.tr
                    key={rule.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-700/50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">#{rule.id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">
                        {rule.model_name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{rule.field}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-medium inline-flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                        {rule.leverage}x
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => onEdit(rule)}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          ویرایش
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          disabled={deletingId === rule.id}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === rule.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              حذف...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              حذف
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default RulesList;
